import { useRef, useState } from 'react';
import { Animated, Platform, Pressable, type ViewStyle } from 'react-native';
import { Box, HStack, Text } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { TimeRange } from '../hooks/useAnalyticsData';

interface TimeRangeSelectorProps {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
}

// Exported so a test can assert that every range on offer resolves to a real
// window. A "Custom" option used to sit here with no date picker and no backend
// support behind it; the tabs coerced it to 30 days, so the selector stayed
// highlighted on Custom while the screen showed a different range.
export const TIME_RANGE_OPTIONS: readonly { value: TimeRange; labelKey: string }[] = [
  { value: '7d', labelKey: 'common.timeRanges.sevenDays' },
  { value: '30d', labelKey: 'common.timeRanges.thirtyDays' },
  { value: '90d', labelKey: 'common.timeRanges.ninetyDays' },
];

function OptionButton({
  option,
  isSelected,
  onPress,
}: Readonly<{
  option: { value: TimeRange; label: string };
  isSelected: boolean;
  onPress: () => void;
}>) {
  const [isHovered, setIsHovered] = useState(false);
  const scaleAnim = useRef(new Animated.Value(1)).current;

  const handlePressIn = () => {
    Animated.spring(scaleAnim, {
      toValue: 0.95,
      useNativeDriver: true,
      speed: 50,
      bounciness: 4,
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scaleAnim, {
      toValue: 1,
      useNativeDriver: true,
      speed: 50,
      bounciness: 4,
    }).start();
  };

  const webHoverStyle = Platform.select({
    web: {
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      ...(!isSelected &&
        isHovered && {
          backgroundColor: colors.surface.row,
        }),
    } as ViewStyle,
    default: {},
  });

  const hoverProps = Platform.select({
    web: {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
    },
    default: {},
  });

  const textColor = isSelected ? colors.primary[400] : colors.text.secondary;

  return (
    <Pressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      accessibilityRole="button"
      accessibilityState={{ selected: isSelected }}
      {...hoverProps}
    >
      <Animated.View
        style={{ transform: [{ scale: scaleAnim }], borderRadius: tokens.radius.full }}
      >
        <Box
          backgroundColor={isSelected ? colors.primary.muted : colors.transparent}
          borderWidth={1}
          borderColor={isSelected ? colors.primary.borderActive : colors.transparent}
          paddingX="lg"
          paddingY="sm"
          style={[webHoverStyle, { borderRadius: tokens.radius.full }]}
        >
          <Text
            color={textColor}
            style={{
              fontWeight: isSelected ? '600' : '500',
              letterSpacing: 0.4,
              fontSize: 13,
            }}
          >
            {option.label}
          </Text>
        </Box>
      </Animated.View>
    </Pressable>
  );
}

export function TimeRangeSelector({ value, onChange }: Readonly<TimeRangeSelectorProps>) {
  const { t } = useTranslation();
  const options = TIME_RANGE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }));

  return (
    <Box alignSelf="flex-start">
      <Box
        backgroundColor={colors.surface.row}
        borderWidth={1}
        borderColor={colors.border.subtle}
        style={{ borderRadius: tokens.radius.full, padding: tokens.spacing.xs }}
      >
        <HStack spacing="xs">
          {options.map((option) => (
            <OptionButton
              key={option.value}
              option={option}
              isSelected={value === option.value}
              onPress={() => onChange(option.value)}
            />
          ))}
        </HStack>
      </Box>
    </Box>
  );
}
